import { ViewModel } from "../../CoreTypes";
import { LocalizedString } from "../../Localization/ILocalizer";
import { IterUtils } from "../../../../../../Common/IterableUtils";
import { isLengthGreaterThenOrEqual } from "../../../../../../Common/TypingUtils";

import { CheckType, IValidationContext, ValidationResult, ValidationResults } from "./Bases/IValueValidator";
import { ValueValidatorBase } from "./Bases/ValueValidatorBase";

interface AttachmentValidationOptions {
    extentions: string[];
    maxSize: number;
    multiple: boolean;
}

export class AttachmentsValueValidator extends ValueValidatorBase {
    private readonly checkType: CheckType;
    private readonly options: AttachmentValidationOptions;
    private readonly message: undefined | LocalizedString;

    public constructor(
        options: AttachmentValidationOptions,
        checkType: CheckType,
        message: undefined | LocalizedString
    ) {
        super();
        this.checkType = checkType;
        this.options = options;
        this.message = message;
    }

    public validate(context: IValidationContext, nodeViewModel: ViewModel): ValidationResult {
        const state = context.store.getState();
        const { error, value } = nodeViewModel;

        if (value == undefined) {
            return ValidationResults.Valid;
        }
        if (error && error.length) {
            return ValidationResults.Invalid(this.getMessage(context, this.message), this.checkType);
        }

        const { extentions, maxSize } = this.options;
        const meta = state.attachments.byId[value]?.meta;
        if (meta == undefined) {
            return ValidationResults.Valid;
        }
        const { fileName, fileSize } = meta;
        const extensionError = this.checkExtension(fileName, extentions);
        if (extensionError != undefined) {
            return ValidationResults.Invalid(extensionError, this.checkType);
        }
        const sizeError = this.checkSize(fileSize, maxSize);
        if (sizeError != undefined) {
            return ValidationResults.Invalid(sizeError, this.checkType);
        }

        return ValidationResults.Valid;
    }

    private checkExtension(fileName: string, acceptedExtensions: readonly string[]): string | undefined {
        const fileNameTokens = fileName.split(".");
        const extension = isLengthGreaterThenOrEqual(fileNameTokens, 2) ? IterUtils.last(fileNameTokens) : "";

        if (!acceptedExtensions.includes(extension.toLowerCase())) {
            return "Неправильный формат файла";
        }
        return undefined;
    }

    private checkSize(fileSize: number, maxSize: number): undefined | string {
        if (fileSize < 0) {
            return "Не удалось получить размер файла";
        }
        if (fileSize === 0) {
            return "Содержимое файла не может быть пустым";
        }
        if (fileSize > maxSize * 1024 * 1024) {
            return this.options.multiple
                ? `Файл слишком большой, попробуйте отправить частями (максимальный размер ${maxSize} Мб)`
                : `Файл слишком большой, попробуйте уменьшить размер до ${maxSize} Мб и прикрепить заново`;
        }
        return undefined;
    }
}
